import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router';

import type { AuthEnvironment } from '../../auth/types';
import { useDataSource, type CapabilityId } from '../../config/capabilities';

export interface FeatureScaffoldPageProps {
  readonly capability: CapabilityId;
  readonly labelKey: string;
}

export function FeatureScaffoldPage({ capability, labelKey }: FeatureScaffoldPageProps) {
  const { t } = useTranslation();
  const environment = useOutletContext<AuthEnvironment>();
  const dataSource = useDataSource(capability, environment);

  return (
    <Box sx={{ marginInline: 'auto', maxWidth: 1120 }}>
      <Stack spacing={1}>
        <Typography component="h1" variant="h4">
          {t(labelKey)}
        </Typography>
        <Typography color="text.secondary">{t(`scaffold.scopes.${capability}`)}</Typography>
      </Stack>

      <Paper component="section" sx={{ border: 1, borderColor: 'divider', mt: 3, p: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Status label={t('dataSource.label')}>
            <Chip
              color={
                dataSource === 'mock' ? 'secondary' : dataSource === 'real' ? 'success' : 'default'
              }
              label={t(`dataSource.${dataSource}`)}
              size="small"
              sx={
                dataSource === 'mock'
                  ? {
                      bgcolor: 'mockData.light',
                      color: 'mockData.dark',
                    }
                  : undefined
              }
            />
          </Status>
          <Status label={t('scaffold.capability')}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Chip
                color="success"
                label={t('scaffold.knownCapability')}
                size="small"
                variant="outlined"
              />
              <Typography component="code" variant="body2">
                {capability}
              </Typography>
            </Stack>
          </Status>
        </Stack>
        <Typography color="text.secondary" sx={{ mt: 3 }} variant="body2">
          {t('scaffold.readOnly')}
        </Typography>
      </Paper>
    </Box>
  );
}

function Status({
  children,
  label,
}: {
  readonly children: React.ReactNode;
  readonly label: string;
}) {
  return (
    <Box sx={{ flex: 1 }}>
      <Typography color="text.secondary" component="div" gutterBottom variant="caption">
        {label}
      </Typography>
      {children}
    </Box>
  );
}
